import invariant from "tiny-invariant";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import CombineOperator from "@mapmycustomers/shared/types/viewModel/internalModel/CombineOperator";
import {
  CombinedCondition,
  FilterModel,
  LegacyFilterCondition,
  LegacySimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import PlatformCombineOperator from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformCombineOperator";
import PlatformFilterModel, {
  AreaCondition,
  Condition,
  PlatformFilterCondition,
  PlatformSimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { isDefined, isNotEmpty } from "@mapmycustomers/shared/util/assert";

import { EntityTypeWithFieldModel } from "@app/util/fieldModel/getByEntityType";
import { createEmptyFilterModel } from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import { isCombinedPlatformCondition, isLegacySimpleCondition } from "@app/util/viewModel/assert";
import { getFieldByPlatformName } from "@app/util/viewModel/getFieldByPlatformName";

import { SET_FILTER_OPERATOR } from "./const";
import parseAreaCondition from "./converters/parseAreaCondition";
import parseCadenceStatusCondition from "./converters/parseCadenceStatusCondition";
import parseEmptyCondition from "./converters/parseEmptyCondition";
import parseIntervalCondition from "./converters/parseIntervalCondition";
import parseLegacyCondition from "./converters/parseLegacyCondition";
import parseRangeCondition from "./converters/parseRangeCondition";
import parseRottingDaysOutCondition from "./converters/parseRottingDaysOutCondition";
import { improveFilterCondition } from "./improveFilterCondition";
import {
  canBeMetaField,
  isEmptyCondition,
  isInAreaCondition,
  isInRangeCondition,
  isIntervalCondition,
  isLegacyCondition,
} from "./util";

export const platformFilterOperatorToFilterOperatorMap: Partial<{
  [key in PlatformFilterOperator]: FilterOperator;
}> = {
  [PlatformFilterOperator.CONTAINS]: FilterOperator.CONTAINS,
  [PlatformFilterOperator.DOES_NOT_END_WITH]: FilterOperator.DOES_NOT_END_WITH,
  [PlatformFilterOperator.DOES_NOT_START_WITH]: FilterOperator.DOES_NOT_START_WITH,
  [PlatformFilterOperator.ENDS_WITH]: FilterOperator.ENDS_WITH,
  [PlatformFilterOperator.EQUALS]: FilterOperator.EQUALS,
  [PlatformFilterOperator.GREATER_THAN]: FilterOperator.GREATER_THAN,
  [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: FilterOperator.GREATER_THAN_OR_EQUAL,
  [PlatformFilterOperator.LESS_THAN]: FilterOperator.LESS_THAN,
  [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: FilterOperator.LESS_THAN_OR_EQUAL,
  [PlatformFilterOperator.NOT_CONTAINS]: FilterOperator.NOT_CONTAINS,
  [PlatformFilterOperator.NOT_EQUAL]: FilterOperator.NOT_EQUAL,
  [PlatformFilterOperator.STARTS_WITH]: FilterOperator.STARTS_WITH,
} as const;

export const platformFilterOperatorToFilterOperatorMapForDates: Partial<{
  [key in PlatformFilterOperator]: FilterOperator;
}> = {
  [PlatformFilterOperator.EQUALS]: FilterOperator.ON,
  [PlatformFilterOperator.GREATER_THAN]: FilterOperator.AFTER,
  [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: FilterOperator.ON_OR_AFTER,
  [PlatformFilterOperator.LESS_THAN]: FilterOperator.BEFORE,
  [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: FilterOperator.ON_OR_BEFORE,
  [PlatformFilterOperator.NOT_EQUAL]: FilterOperator.NOT_ON,
} as const;

export const platformCombineOperatorToFilterCombineOperator: {
  [key in PlatformCombineOperator]: CombineOperator;
} = {
  $and: "AND",
  $not: "NOT",
  $or: "OR",
} as const;

const cleanupPlatformCondition = (platformCondition: any): void => {
  // remove $dateOnly field as we don't use it, but it interferes keys count calculations
  // used below in isInRangeCondition or in filterOperators.length > 1
  // Same for $offset and $timezone
  if (typeof platformCondition === "object") {
    for (const field of ["$dateOnly", "$offset", "$timezone"]) {
      delete platformCondition[field];
    }
  }
};

export const getRegularFieldConditionValue = (
  field: IField,
  condition: PlatformSimpleCondition
): LegacyFilterCondition | undefined => {
  const platformFilterName = field.platformFilterNames.find((name) => name in condition);
  const platformCondition: any | AreaCondition | Condition = platformFilterName
    ? condition[platformFilterName]
    : undefined;

  if (!isNotEmpty(platformCondition)) {
    return undefined;
  }

  cleanupPlatformCondition(platformCondition);

  const isDateField = [FieldType.DATE, FieldType.DATE_TIME].includes(field.type);

  if (typeof platformCondition !== "object" || platformCondition === null) {
    if (isDateField) {
      return { operator: FilterOperator.ON, value: platformCondition };
    }
    return field.type === FieldType.BOOLEAN
      ? {
          operator: platformCondition ? FilterOperator.IS_TRUE : FilterOperator.IS_FALSE,
          value: undefined,
        }
      : { operator: FilterOperator.EQUALS, value: platformCondition };
  }

  if (field.hasFeature(FieldFeature.FREQUENCY_STATUS_FIELD)) {
    return parseCadenceStatusCondition(platformCondition);
  }

  if (field.hasFeature(FieldFeature.ROTTING_DAYS_OUT_FIELD)) {
    return parseRottingDaysOutCondition(platformCondition);
  }

  if (isInRangeCondition(platformCondition)) {
    return parseRangeCondition(platformCondition);
  }

  if (isInAreaCondition(platformCondition)) {
    return parseAreaCondition(platformCondition);
  }

  if (isEmptyCondition(platformCondition)) {
    return parseEmptyCondition(platformCondition);
  }

  if (isLegacyCondition(platformCondition)) {
    return parseLegacyCondition(field, platformCondition);
  }

  if (isIntervalCondition(platformCondition)) {
    return parseIntervalCondition(platformCondition);
  }

  const filterOperator = Object.keys(platformCondition)[0] as PlatformFilterOperator;
  let value = platformCondition[filterOperator];

  // mobile saved filters tend to have IDs as strings, so let's try to
  // fix this. Only keeping the conversion result if it matches the original
  // value. Need this to prevent conversion of a valid string ids.
  if (field.filterType === FieldType.ID) {
    if (Array.isArray(value)) {
      value = value.map((id) => {
        const numericId = parseInt(id, 10);
        return !isNaN(numericId) && numericId.toString() === id ? numericId : id;
      });
    } else if (typeof value === "string") {
      const numericId = parseInt(value, 10);
      value = !isNaN(numericId) && numericId.toString() === value ? numericId : value;
    }
  }

  switch (filterOperator) {
    case PlatformFilterOperator.CONTAINS:
      return field.hasFeature(FieldFeature.SUPPORTS_SET_FILTER)
        ? { operator: SET_FILTER_OPERATOR, value }
        : { operator: FilterOperator.CONTAINS, value };
    case PlatformFilterOperator.DOES_NOT_END_WITH:
    case PlatformFilterOperator.DOES_NOT_START_WITH:
    case PlatformFilterOperator.ENDS_WITH:
    case PlatformFilterOperator.STARTS_WITH: {
      const operator = platformFilterOperatorToFilterOperatorMap[filterOperator];
      invariant(operator, `Failed to convert "${filterOperator}" into platform operator`);
      return { operator, value };
    }
    case PlatformFilterOperator.EQUALS:
    case PlatformFilterOperator.GREATER_THAN:
    case PlatformFilterOperator.GREATER_THAN_OR_EQUAL:
    case PlatformFilterOperator.LESS_THAN:
    case PlatformFilterOperator.LESS_THAN_OR_EQUAL:
    case PlatformFilterOperator.NOT_EQUAL: {
      const operator = (
        isDateField
          ? platformFilterOperatorToFilterOperatorMapForDates
          : platformFilterOperatorToFilterOperatorMap
      )[filterOperator];
      invariant(operator, `Failed to convert "${filterOperator}" into platform operator`);
      return { operator, value };
    }
    case PlatformFilterOperator.GROUP_IN_ALL:
      return { operator: FilterOperator.IN_ALL, value };
    case PlatformFilterOperator.GROUP_IN_ANY:
      return { operator: SET_FILTER_OPERATOR, value };
    case PlatformFilterOperator.NOT_CONTAINS:
      if (field.hasFeature(FieldFeature.SUPPORTS_SET_FILTER)) {
        return { operator: FilterOperator.NONE_OF, value };
      }
      return { operator: FilterOperator.NOT_CONTAINS, value };
  }

  return undefined;
};

export const getCustomFieldConditionValue = (
  field: IField,
  condition: PlatformSimpleCondition
): LegacySimpleCondition | undefined => {
  const platformFieldName = field.platformFilterNames.find((name) => condition[name]);
  const platformCondition: any | Condition = platformFieldName
    ? condition[platformFieldName]
    : undefined;

  if (!platformCondition) {
    return undefined;
  }
  const isDateOrTimeField = [FieldType.DATE, FieldType.DATE_TIME, FieldType.TIME].includes(
    field.type
  );

  cleanupPlatformCondition(platformCondition);

  const filterOperators =
    typeof platformCondition !== "object" // if it is not an object, it must be a value itself, and the operator is EQ
      ? []
      : Object.keys(platformCondition)
          // skip dataType field, it's not an operator
          .filter((operator) => operator !== "dataType");

  if (isInRangeCondition(platformCondition)) {
    return parseRangeCondition(platformCondition);
  }

  if (isEmptyCondition(platformCondition)) {
    return parseEmptyCondition(platformCondition);
  }

  if (isIntervalCondition(platformCondition)) {
    return parseIntervalCondition(platformCondition);
  }

  invariant(
    filterOperators.length <= 1,
    `More than one filter operator for custom field condition: "${JSON.stringify(condition)}"`
  );
  const filterOperator = filterOperators.length ? filterOperators[0] : FilterOperator.EQUALS;
  const value = filterOperators.length
    ? (platformCondition as any)[filterOperator]
    : platformCondition;

  switch (filterOperator) {
    case PlatformFilterOperator.CONTAINS:
      return field.type === FieldType.LIST || field.hasFeature(FieldFeature.SUPPORTS_SET_FILTER)
        ? { operator: SET_FILTER_OPERATOR, value }
        : { operator: FilterOperator.CONTAINS, value };
    case PlatformFilterOperator.DOES_NOT_END_WITH:
    case PlatformFilterOperator.DOES_NOT_START_WITH:
    case PlatformFilterOperator.ENDS_WITH:
    case PlatformFilterOperator.STARTS_WITH: {
      const operator = platformFilterOperatorToFilterOperatorMap[filterOperator];
      invariant(operator, `Failed to convert "${filterOperator}" into platform operator`);
      return { operator, value };
    }
    case PlatformFilterOperator.EQUALS:
      return field.type === FieldType.BOOLEAN
        ? {
            operator: value ? FilterOperator.IS_TRUE : FilterOperator.IS_FALSE,
            value: undefined,
          }
        : { operator: isDateOrTimeField ? FilterOperator.ON : FilterOperator.EQUALS, value };
    case PlatformFilterOperator.GREATER_THAN:
    case PlatformFilterOperator.GREATER_THAN_OR_EQUAL:
    case PlatformFilterOperator.LESS_THAN:
    case PlatformFilterOperator.LESS_THAN_OR_EQUAL:
    case PlatformFilterOperator.NOT_EQUAL: {
      const operator = (
        isDateOrTimeField
          ? platformFilterOperatorToFilterOperatorMapForDates
          : platformFilterOperatorToFilterOperatorMap
      )[filterOperator];
      invariant(operator, `Failed to convert "${filterOperator}" into platform operator`);
      return { operator, value };
    }
    case PlatformFilterOperator.NOT_CONTAINS:
      if (field.hasFeature(FieldFeature.SUPPORTS_SET_FILTER)) {
        return { operator: FilterOperator.NONE_OF, value };
      }
      return { operator: FilterOperator.NOT_CONTAINS, value };
  }

  return undefined;
};

const ignoreFilters = [
  "includeNotes",
  "includeGroups",
  "includeCustomFields",
  "includeAccessStatus",
  "includeRoutes",
  "includeTerritories",
];

type Candidate = {
  // field name without entity type prefix, matches key if fieldEntityType is undefined
  actualFieldName: string;
  // field, we understood from the object key
  field: IField;
  // field entity type in case it different from the fieldModel's one
  fieldEntityType: EntityTypeWithFieldModel | undefined;
  // object key this field was found for
  key: string;
};

const createCandidatesMap = (
  platformCondition: PlatformSimpleCondition,
  fieldModel: IFieldModel
): Map<string, Candidate> => {
  return new Map(
    Object.keys(platformCondition)
      .map((key) => {
        const { field, fieldEntityType } = getFieldByPlatformName(key, fieldModel);

        let actualFieldName = key;
        if (fieldEntityType) {
          actualFieldName = key.substring(fieldEntityType.length + 1);
        }

        return [key, field ? { actualFieldName, field, fieldEntityType, key } : undefined];
      })
      .filter(([, candidate]) => isDefined(candidate)) as [string, Candidate][]
  );
};

export const addConditionsRecursively = (
  from: PlatformFilterCondition[],
  to: CombinedCondition["conditions"],
  fieldModel: IFieldModel
) => {
  from.forEach((platformCondition) => {
    if (isCombinedPlatformCondition(platformCondition)) {
      const combineOperator = "$and" in platformCondition ? "$and" : "$or";
      const combineCondition = {
        conditions: [],
        operator: platformCombineOperatorToFilterCombineOperator[combineOperator],
      } as CombinedCondition;
      addConditionsRecursively(
        platformCondition[combineOperator]!,
        combineCondition.conditions,
        fieldModel
      );
      if (combineCondition.conditions.length) {
        to.push(combineCondition);
      }
    } else {
      const candidatesMap = createCandidatesMap(platformCondition, fieldModel);

      const candidates = Array.from(candidatesMap.values());
      if (candidates.length < 1) {
        loggingService.warning(`Cannot decode platform condition, unknown field name`, {
          fieldModel,
          keyMap: candidatesMap,
          platformCondition,
        });
        // skipping this condition
        return;
      }
      if (candidates.length > 1) {
        loggingService.warning(`Cannot decode platform condition, multiple candidate fields`, {
          keyMap: Object.fromEntries(candidatesMap),
          platformCondition,
        });
        // skipping such unclear condition
        return;
      }

      const { actualFieldName, field, fieldEntityType, key } = candidates[0];
      const fieldNamePrefix = fieldEntityType ? `${fieldEntityType}.` : "";

      const platformConditionToParse = fieldEntityType
        ? { [actualFieldName]: platformCondition[key] }
        : platformCondition;

      const result = field.convertFromPlatformCondition(platformConditionToParse);
      if (result) {
        if (isLegacySimpleCondition(result)) {
          const simpleCondition = {
            fieldFilterName: `${fieldNamePrefix}${field.filterName}`,
            operator: result.operator,
            value: result.value,
          };
          to.push(improveFilterCondition(simpleCondition, field));
        } else {
          to.push({
            conditions: result.conditions.map((simpleCondition) => ({
              fieldFilterName: `${fieldNamePrefix}${field.filterName}`,
              operator: simpleCondition.operator,
              value: simpleCondition.value,
            })),
            operator: result.operator,
          });
        }
      }
    }
  });
};

export const convertFromPlatformFilterModel = (
  platformFilterModel: PlatformFilterModel,
  fieldModel: IFieldModel,
  includeField: (field: IField) => boolean = () => true
): FilterModel => {
  // console.group("!!! convertFromPlatformFilterModel");

  // remove filters we're not interested in
  const model = { ...platformFilterModel };
  Object.keys(model).forEach((key) => {
    if (ignoreFilters.includes(key)) {
      delete model[key as keyof PlatformFilterModel];
    }
  });
  const filterModel: FilterModel = createEmptyFilterModel();

  const metaFields = fieldModel.fields.filter(
    (field) =>
      field.hasAnyFeature(FieldFeature.FILTERABLE, FieldFeature.FILTERABLE_ON_MAP) &&
      includeField(field) &&
      canBeMetaField(field) &&
      field.isPresentInPlatformCondition(model)
  );

  metaFields.forEach((field) => {
    // look for condition for current field
    // in the root of platform filter if field is a meta field
    const condition = field.getFilterConditionForMetaField(platformFilterModel);
    const result = field.convertFromPlatformCondition(condition);
    if (result) {
      if (isLegacySimpleCondition(result)) {
        filterModel.conditions.push({
          fieldFilterName: field.filterName,
          operator: result.operator,
          value: result.value,
        });
      } else {
        filterModel.conditions.push({
          conditions: result.conditions.map((simpleCondition) => ({
            fieldFilterName: field.filterName,
            operator: simpleCondition.operator,
            value: simpleCondition.value,
          })),
          operator: result.operator,
        });
      }
    }
  });

  const andPresent = "$and" in model;
  const orPresent = "$or" in model;

  // if only OR is present, and we don't have any conditions from meta
  // fields yet, let's change the top-level combine operator to OR
  if (orPresent && !andPresent && !filterModel.conditions.length) {
    filterModel.operator = "OR";
  }

  if (andPresent) {
    addConditionsRecursively(platformFilterModel.$and!, filterModel.conditions, fieldModel);
  }

  if (orPresent) {
    let destination = filterModel.conditions;
    if (filterModel.operator !== "OR") {
      const orCondition = { conditions: [], operator: "OR" } as CombinedCondition;
      filterModel.conditions.push(orCondition);
      destination = orCondition.conditions;
    }

    addConditionsRecursively(platformFilterModel.$or!, destination, fieldModel);
  }

  // console.groupEnd();

  return filterModel;
};
